import axios, { AxiosPromise, AxiosRequestHeaders, AxiosResponse } from 'axios';

let keycloakToken = '';

export const syncToken = (token: string) => {
  keycloakToken = token;
};

export const initAxios = (allowedUrls: string[] = []): void => {
  axios.defaults.headers.common['Content-Type'] = 'application/json';
  axios.interceptors.request.use(
    axiosRequestConfig => {
      if (!axiosRequestConfig.headers) {
        axiosRequestConfig.headers = {} as AxiosRequestHeaders;
      }
      if (
        keycloakToken &&
        (allowedUrls.length === 0 || allowedUrls.some(url => axiosRequestConfig.url?.includes(url)))
      ) {
        axiosRequestConfig.headers.authorization = `Bearer ${keycloakToken}`;
      }
      return axiosRequestConfig;
    },
    error => Promise.reject(error),
  );
};

export interface BaseRequestParameters {
  csrfToken: string;
}

// Factory function to produce a function doing a POST to the given url, with the data passed as URL parameters.
export function makePostWithParameters<Parameters extends BaseRequestParameters, ResponseType>(
  url: string,
) {
  return async ({ csrfToken, ...params }: Parameters) => {
    const result: AxiosResponse<ResponseType> = await axios({
      url,
      params,
      method: 'post',
      headers: { 'X-CSRF-Token': csrfToken },
    });
    // just in case axios returns null/undefined
    if (result) {
      return result.data;
    } else {
      throw new Error(`Internal error while posting to ${url}`);
    }
  };
}

export interface RequestData<Data> {
  csrfToken: string;
  data: Data;
}

// Factory function to produce a function doing a POST to the given url, with the data passed in the body.
export function makePostWithData<Data, ResponseType>(url: string) {
  return async ({ csrfToken, data }: RequestData<Data>) => {
    const result: AxiosResponse<ResponseType> = await axios({
      url,
      data,
      method: 'post',
      headers: { 'X-CSRF-Token': csrfToken },
    });
    // just in case axios returns null/undefined
    if (result) {
      return result.data;
    } else {
      throw new Error('Internal error while saving Adyen credit card');
    }
  };
}

/**
 * Factory function to make a POST request to the given URL, include the given body and request params.
 * This function is introduced to replace the use of "makePostWithData" and "makePostWithParameters" since
 * they are using a "higher-order functions pattern" (function returns function) that makes the code harder
 * to understand.
 */
export const axiosPost = (
  url: string,
  csrfToken: string,
  requestBody?: any,
  requestParams?: any,
): AxiosPromise => {
  return axios({
    url,
    method: 'POST',
    data: requestBody,
    params: requestParams,
    headers: { 'X-CSRF-Token': csrfToken },
  });
};

export const axiosGet = (
  url: string,
  params?: any,
  headers?: Record<string, string>,
): AxiosPromise => {
  return axios({
    method: 'GET',
    url,
    params,
    headers,
  });
};
